import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, set, push } from 'firebase/database';
import { db } from '../lib/firebase.js';
import { useCurrentContext } from '../lib/useCurrentContext.jsx';
import { useAuth } from '../lib/useAuth.jsx';
import {
  ASSESSMENT_SCREENS,
  TOTAL_QUESTIONS,
  TOTAL_POINTS,
  computeResults,
  scoreReaction,
  matchesAnswer,
  pickAnimal,
} from '../lib/assessment.js';
import './Assessment.css';

// The cognitive assessment.
//
// This component is used in two modes:
//   - Resident self-administered: at /onboarding/assessment, the
//     signed-in user (community resident OR individual subscriber)
//     takes their own assessment. The target base path comes from
//     useCurrentContext — /brain_health/accounts/... for residents,
//     /brain_health/users/... for individual subscribers.
//   - Staff-administered: at /admin/communities/:code/residents/:id/assessment,
//     the signed-in admin takes the assessment on behalf of a resident.
//     The target is identified by `residentOverride` prop and is by
//     definition a community resident — the staff flow doesn't apply
//     to individual subscribers (they self-administer).
//
// Both modes save the same record shape; the difference is the
// `administered_by` field (and the base path, in the case of
// individual subscribers in resident mode).
//
// On completion, navigates to /onboarding/results (or the staff
// equivalent), passing the computed score so the results page can
// render without re-loading from Firebase immediately.
//
// In v1, progress is held in component state only — no incremental
// save to Firebase. If the user navigates away mid-assessment, they
// start over next time. This is by design (Step 6 plan, option C).
export default function Assessment({ mode = 'resident', residentOverride = null }) {
  const navigate = useNavigate();
  const ctx = useCurrentContext();
  const { user } = useAuth();

  // PREVIEW — remove when no longer needed.
  // When rendered under PreviewContextProvider, run the full flow with
  // no Firebase writes and route to the preview results page.
  const isPreview = !!ctx.isPreview;

  // Which account/user is this assessment for? In resident mode, the
  // signed-in identity (community resident or individual subscriber).
  // In staff mode, the admin-route passes residentOverride.
  //
  // The base path tells writes where to land. Staff mode is always
  // /accounts/ because the staff flow exists only for community
  // residents. Resident mode uses whichever root the current identity
  // lives under.
  const targetAccount = mode === 'staff' ? residentOverride : ctx.record;
  const targetAccountId = targetAccount?.id;
  const targetBasePath =
    mode === 'staff'
      ? (residentOverride?.id
          ? `brain_health/accounts/${residentOverride.id}`
          : null)
      : ctx.rtdbPath;
  const firstName = (targetAccount?.display_name || '').split(' ')[0] || 'there';

  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Number of scored questions answered so far (for progress display).
  const questionNum = useMemo(() => {
    const SCORED = ['mcq', 'reaction', 'text-entry'];
    return ASSESSMENT_SCREENS
      .slice(0, index + 1)
      .filter((s) => SCORED.includes(s.type)).length;
  }, [index]);

  const screen = ASSESSMENT_SCREENS[index];

  function advance() {
    setIndex((i) => Math.min(i + 1, ASSESSMENT_SCREENS.length - 1));
  }

  function recordAnswer(s, chosen) {
    const earned = chosen === s.correctIndex ? s.points : 0;
    setAnswers((prev) => [
      ...prev,
      { qNum: s.qNum, domain: s.domain, earned, possible: s.points },
    ]);
    advance();
  }

  // General scoring path for non-MCQ screens (reaction, text-entry).
  // `meta` carries any extra data to persist (e.g. reaction_ms).
  function recordScore(s, earned, meta) {
    setAnswers((prev) => [
      ...prev,
      {
        qNum: s.qNum,
        domain: s.domain,
        earned,
        possible: s.points,
        ...(meta ? { meta } : {}),
      },
    ]);
    advance();
  }

  // When we reach the results screen, save to Firebase and route to
  // results/goals. We do this in an effect so we don't perform side
  // effects during render.
  useEffect(() => {
    if (screen.type !== 'results' || saving) return;

    // PREVIEW — remove when no longer needed.
    // Skip all Firebase writes. Compute results in memory and carry
    // them to the preview results page via router state.
    if (isPreview) {
      setSaving(true);
      const results = computeResults(answers);
      navigate('/assessment/preview/results', {
        replace: true,
        state: {
          preview: true,
          score: results.score,
          level: results.level,
          pointsByDomain: results.pointsByDomain,
        },
      });
      return;
    }

    if (!targetAccountId || !targetBasePath) return;
    setSaving(true);
    saveAndContinue();

    async function saveAndContinue() {
      try {
        const results = computeResults(answers);
        const now = Date.now();

        const currentRecord = {
          score: results.score,
          level: results.level,
          date: now,
          administered_by: mode === 'staff' ? (user?.email || 'staff') : 'self',
        };

        // Per-domain breakdown — captured at assessment time so the
        // dashboard can surface weak areas without recomputing from
        // raw answers (which we don't persist).
        //
        // Stored as nested keys (domain → {earned, possible}) so the
        // database rules can validate each field individually.
        if (results.pointsByDomain && Object.keys(results.pointsByDomain).length > 0) {
          currentRecord.domain_breakdown = results.pointsByDomain;
        }

        // Stage 2: persist the raw reaction-time average (ms) if the
        // reaction screen produced one. Stored as a top-level field on
        // the current record for later trend tracking. Requires the
        // matching allowance in database.rules.json (reaction_ms).
        const reactionAnswer = answers.find((a) => a.meta && a.meta.reaction_ms != null);
        if (reactionAnswer) {
          currentRecord.reaction_ms = reactionAnswer.meta.reaction_ms;
        }

        // Write current assessment (overwrites previous current).
        await set(
          ref(db, `${targetBasePath}/assessment/current`),
          currentRecord
        );

        // Append to history.
        const historyRef = push(
          ref(db, `${targetBasePath}/assessment/history`)
        );
        await set(historyRef, currentRecord);

        // Update last_active timestamp.
        await set(
          ref(db, `${targetBasePath}/last_active`),
          now
        );

        // Route to the appropriate next step.
        if (mode === 'staff') {
          // Staff flow ends here — return them to the resident roster
          // with a results summary in URL state.
          navigate(
            `/admin/communities/${encodeURIComponent(targetAccount.community_code)}/residents`,
            {
              replace: true,
              state: {
                lastAssessment: {
                  name: targetAccount.display_name,
                  score: results.score,
                  level: results.level,
                },
              },
            }
          );
        } else {
          // Resident flow (community OR individual): show results,
          // then they continue to goals.
          navigate('/onboarding/results', { replace: true });
        }
      } catch (err) {
        setError(err.message);
        setSaving(false);
      }
    }
  }, [screen.type, saving, targetAccountId, targetBasePath, answers, mode, user, navigate, targetAccount, isPreview]);

  if (!targetAccount) {
    return (
      <div className="container assessment">
        <p className="assessment-meta">Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container assessment">
        <div className="assessment-error" role="alert">
          <p className="assessment-error-line">
            Something went wrong saving your results.
          </p>
          <p className="assessment-error-help">{error}</p>
          <button
            type="button"
            className="btn"
            onClick={() => { setError(null); setSaving(false); setIndex((i) => i); }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container assessment">
      <div className="assessment-bar">
        <span className="eyebrow">
          {mode === 'staff'
            ? `Staff-Administered Assessment · ${targetAccount.display_name}`
            : `Onboarding · Step 1 of 2`}
        </span>
        {screen.type === 'mcq' || screen.type === 'reaction' || screen.type === 'text-entry' ? (
          <span className="assessment-progress">
            Question {questionNum} of {TOTAL_QUESTIONS}
          </span>
        ) : null}
      </div>

      <div className="assessment-card">
        {screen.type === 'welcome' ? (
          <WelcomeScreen
            firstName={firstName}
            isStaff={mode === 'staff'}
            onStart={advance}
          />
        ) : null}

        {screen.type === 'mcq' ? (
          <McqScreen
            key={`mcq-${index}`}
            screen={screen}
            onAnswer={(chosen) => recordAnswer(screen, chosen)}
          />
        ) : null}

        {screen.type === 'encode' ? (
          <EncodeScreen screen={screen} onDone={advance} />
        ) : null}

        {/* Stage 2 — reaction-time test */}
        {screen.type === 'reaction' ? (
          <ReactionScreen
            key={`reaction-${index}`}
            screen={screen}
            onComplete={(earned, meta) => recordScore(screen, earned, meta)}
          />
        ) : null}

        {/* Stage 2 — name-the-animal */}
        {screen.type === 'text-entry' ? (
          <TextEntryScreen
            key={`text-${index}`}
            screen={screen}
            onSubmit={(earned, meta) => recordScore(screen, earned, meta)}
          />
        ) : null}

        {screen.type === 'results' ? (
          <div className="assessment-saving">
            <p className="assessment-meta">Saving your results…</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// =================================================================
// Welcome screen
// =================================================================

function WelcomeScreen({ firstName, isStaff, onStart }) {
  return (
    <div className="assessment-welcome">
      <h1 className="assessment-welcome-title">
        {isStaff
          ? 'A brief check-in for the mind.'
          : `Hello, ${firstName}.`}
      </h1>
      <p className="assessment-welcome-lede">
        This short assessment helps us understand which brain health
        activities will be the best fit for {isStaff ? 'this resident' : 'you'}.
        There are no <em>wrong</em> answers — just answer to the best
        of {isStaff ? 'their' : 'your'} ability.
      </p>

      <div className="assessment-meta-grid">
        <div className="assessment-meta-item">
          <span className="assessment-meta-num">~7</span>
          <span className="assessment-meta-label">Minutes</span>
        </div>
        <div className="assessment-meta-item">
          <span className="assessment-meta-num">{TOTAL_QUESTIONS}</span>
          <span className="assessment-meta-label">Questions</span>
        </div>
        <div className="assessment-meta-item">
          <span className="assessment-meta-num">{TOTAL_POINTS}</span>
          <span className="assessment-meta-label">Total Points</span>
        </div>
      </div>

      <button type="button" className="btn assessment-start-btn" onClick={onStart}>
        Begin Assessment
      </button>
    </div>
  );
}

// =================================================================
// MCQ screen
// =================================================================

const OPTION_LETTERS = ['a', 'b', 'c', 'd', 'e'];

function McqScreen({ screen, onAnswer }) {
  // Resolve dynamic options (date / day questions use a function).
  const options = useMemo(
    () => (screen.optionsFn ? screen.optionsFn() : screen.options),
    [screen]
  );

  return (
    <div className="assessment-q">
      <div className="assessment-q-label">
        Question {screen.qNum}
        {screen.points
          ? ` · ${screen.points} ${screen.points === 1 ? 'point' : 'points'}`
          : ''}
      </div>
      <div className="assessment-q-prompt">{screen.prompt}</div>

      {screen.showClock ? (
        <div className="assessment-clock-wrap">
          <ClockSvg />
          <div className="assessment-q-context assessment-q-context-inline">
            {screen.context}
          </div>
        </div>
      ) : screen.context ? (
        <div className="assessment-q-context assessment-q-context-math">
          {screen.context}
        </div>
      ) : null}

      <div className="assessment-options" role="radiogroup">
        {options.map((opt, i) => (
          <button
            key={i}
            type="button"
            className="assessment-option"
            role="radio"
            aria-checked="false"
            onClick={(e) => {
              // Drop focus immediately so mobile browsers don't keep
              // this button visually highlighted after the next
              // question renders. Combined with the `key` on McqScreen
              // in the parent (which remounts the whole question on
              // change), this prevents the "previous answer still
              // highlighted" effect that React's DOM-reuse can cause.
              e.currentTarget.blur();
              onAnswer(i);
            }}
          >
            <span className="assessment-option-letter">
              {OPTION_LETTERS[i]}
            </span>
            <span>{opt}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// =================================================================
// Encode screen — shows content for a fixed duration with countdown
// =================================================================

function EncodeScreen({ screen, onDone }) {
  const [remaining, setRemaining] = useState(screen.duration);

  // Ref to onDone so the effect doesn't re-run on every parent re-render.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  // Guard so onDone fires exactly once, whether triggered by the timer
  // reaching zero or by the resident tapping "I'm done reading".
  const doneRef = useRef(false);
  const intervalRef = useRef(null);

  function finish() {
    if (doneRef.current) return;
    doneRef.current = true;
    clearInterval(intervalRef.current);
    onDoneRef.current?.();
  }

  useEffect(() => {
    doneRef.current = false;
    setRemaining(screen.duration);
    intervalRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          finish();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen.duration, screen.variant]);

  const RADIUS = 42;
  const CIRC = 2 * Math.PI * RADIUS;
  const offset = CIRC * (1 - remaining / screen.duration);

  return (
    <div className="assessment-encode">
      <div className="assessment-encode-instruction">{screen.instruction}</div>

      {screen.variant === 'words' ? (
        <div className="assessment-word-grid">
          {screen.words.map((w) => (
            <div key={w} className="assessment-word-chip">{w}</div>
          ))}
        </div>
      ) : null}

      {screen.variant === 'math' ? (
        <div className="assessment-math-display">
          You had <span className="assessment-dollar">$50</span> and
          bought some oranges for <span className="assessment-dollar">$5</span>{' '}
          and some bananas for <span className="assessment-dollar">$7</span>.
        </div>
      ) : null}

      {screen.variant === 'story' ? (
        <div className="assessment-story">{screen.story}</div>
      ) : null}

      <div className="assessment-countdown">
        <svg className="assessment-countdown-svg" viewBox="0 0 96 96">
          <circle
            className="assessment-countdown-bg"
            cx="48"
            cy="48"
            r={RADIUS}
          />
          <circle
            className="assessment-countdown-fg"
            cx="48"
            cy="48"
            r={RADIUS}
            strokeDasharray={CIRC.toFixed(2)}
            strokeDashoffset={offset.toFixed(2)}
          />
        </svg>
        <div className="assessment-countdown-num">{remaining}</div>
      </div>
      <div className="assessment-countdown-label">Seconds remaining</div>

      {screen.skippable ? (
        <button
          type="button"
          className="btn btn-secondary assessment-encode-skip"
          onClick={finish}
          style={{ marginTop: '1.5rem' }}
        >
          I'm done reading
        </button>
      ) : null}
    </div>
  );
}

// =================================================================
// Clock SVG — shown for time-arithmetic questions
// =================================================================

function ClockSvg() {
  // 10:10 clock. Hours pin at 1:00 position, minute at 2:00 position.
  // Using fixed precision to avoid SVG sub-pixel rendering differences.
  const cx = 70;
  const cy = 70;
  const ticks = [];
  for (let i = 0; i < 12; i++) {
    const angle = (i * 30 - 90) * (Math.PI / 180);
    const x1 = cx + Math.cos(angle) * 56;
    const y1 = cy + Math.sin(angle) * 56;
    const x2 = cx + Math.cos(angle) * 62;
    const y2 = cy + Math.sin(angle) * 62;
    ticks.push(
      <line
        key={i}
        className="assessment-clock-tick"
        x1={x1.toFixed(1)}
        y1={y1.toFixed(1)}
        x2={x2.toFixed(1)}
        y2={y2.toFixed(1)}
      />
    );
  }

  // 10:10
  // Hour hand at 10 (-60deg from 12), minute hand at 2 (60deg from 12)
  const hourAngle = (10 * 30 + (10 / 60) * 30 - 90) * (Math.PI / 180);
  const minuteAngle = (10 * 6 - 90) * (Math.PI / 180);
  const hx = cx + Math.cos(hourAngle) * 36;
  const hy = cy + Math.sin(hourAngle) * 36;
  const mx = cx + Math.cos(minuteAngle) * 50;
  const my = cy + Math.sin(minuteAngle) * 50;

  return (
    <svg
      className="assessment-clock"
      viewBox="0 0 140 140"
      aria-label="Clock showing 10:10"
    >
      <circle className="assessment-clock-face" cx={cx} cy={cy} r="64" />
      {ticks}
      <line
        className="assessment-clock-hand assessment-clock-hand-hour"
        x1={cx}
        y1={cy}
        x2={hx.toFixed(1)}
        y2={hy.toFixed(1)}
      />
      <line
        className="assessment-clock-hand assessment-clock-hand-minute"
        x1={cx}
        y1={cy}
        x2={mx.toFixed(1)}
        y2={my.toFixed(1)}
      />
      <circle className="assessment-clock-pin" cx={cx} cy={cy} r="4" />
    </svg>
  );
}

// =================================================================
// Reaction-time screen (Stage 2)
// =================================================================
// Runs `screen.trials` trials. Each trial: a purple "wait" state for
// a random delay, then the screen turns bright yellow ("TAP NOW").
// We measure ms from yellow→tap. Tapping during the wait is a false
// start (doesn't count; the trial restarts). After all trials, the
// average of valid latencies is bucketed into points by scoreReaction.
//
// Accessibility: the wait delay is randomized (1.2–3.5s) so it can't
// be anticipated, false starts are forgiven rather than penalized,
// and there's no per-trial time pressure beyond the tap itself.

const REACTION_STATES = { idle: 'idle', waiting: 'waiting', go: 'go', tooSoon: 'tooSoon', done: 'done' };

function ReactionScreen({ screen, onComplete }) {
  const totalTrials = screen.trials || 5;
  const [state, setState] = useState(REACTION_STATES.idle);
  const [trialsDone, setTrialsDone] = useState(0);
  const [lastMs, setLastMs] = useState(null);

  const resultsRef = useRef([]);          // valid latencies (ms)
  const goAtRef = useRef(0);              // timestamp when it turned yellow
  const timerRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Clean up any pending timer on unmount.
  useEffect(() => () => clearTimeout(timerRef.current), []);

  function startTrial() {
    setState(REACTION_STATES.waiting);
    setLastMs(null);
    // Random 1.2–3.5s delay before "go".
    const delay = 1200 + Math.random() * 2300;
    timerRef.current = setTimeout(() => {
      goAtRef.current = performance.now();
      setState(REACTION_STATES.go);
    }, delay);
  }

  function finishOrNext(updatedResults) {
    if (updatedResults.length >= totalTrials) {
      const { averageMs, points } = scoreReaction(updatedResults);
      setState(REACTION_STATES.done);
      // Brief pause so the resident sees the final result before moving on.
      timerRef.current = setTimeout(() => {
        onCompleteRef.current?.(
          points,
          averageMs != null ? { reaction_ms: averageMs } : undefined
        );
      }, 900);
    } else {
      // Next trial after a short beat.
      timerRef.current = setTimeout(startTrial, 700);
    }
  }

  function handleTap() {
    if (state === REACTION_STATES.idle) {
      startTrial();
      return;
    }
    if (state === REACTION_STATES.waiting) {
      // False start — forgive it, restart this same trial.
      clearTimeout(timerRef.current);
      setState(REACTION_STATES.tooSoon);
      timerRef.current = setTimeout(startTrial, 1100);
      return;
    }
    if (state === REACTION_STATES.go) {
      const ms = Math.round(performance.now() - goAtRef.current);
      const next = [...resultsRef.current, ms];
      resultsRef.current = next;
      setLastMs(ms);
      setTrialsDone(next.length);
      finishOrNext(next);
      return;
    }
  }

  const tapClass =
    state === REACTION_STATES.go
      ? 'assessment-reaction-pad assessment-reaction-go'
      : state === REACTION_STATES.tooSoon
      ? 'assessment-reaction-pad assessment-reaction-toosoon'
      : 'assessment-reaction-pad assessment-reaction-wait';

  const padBg =
    state === REACTION_STATES.go
      ? '#FFD400'
      : state === REACTION_STATES.tooSoon
      ? '#B81E8F'
      : '#690FC9';
  const padColor = state === REACTION_STATES.go ? '#33143E' : '#FBF6EE';

  return (
    <div className="assessment-reaction">
      <div className="assessment-q-label">Reaction Time</div>
      <div className="assessment-q-prompt">{screen.instruction}</div>

      <button
        type="button"
        className={tapClass}
        onClick={handleTap}
        style={{
          background: padBg,
          color: padColor,
          border: 'none',
          borderRadius: '20px',
          width: '100%',
          minHeight: '220px',
          margin: '1.5rem 0',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-serif, Georgia, serif)',
          fontSize: '1.8rem',
          transition: 'background 120ms ease',
          userSelect: 'none',
          WebkitTapHighlightColor: 'transparent',
        }}
        aria-label={
          state === REACTION_STATES.go
            ? 'Tap now'
            : state === REACTION_STATES.waiting
            ? 'Wait for yellow'
            : 'Reaction pad'
        }
      >
        {state === REACTION_STATES.idle ? (
          <span className="assessment-reaction-text">Tap to start</span>
        ) : state === REACTION_STATES.waiting ? (
          <span className="assessment-reaction-text">Wait for it…</span>
        ) : state === REACTION_STATES.go ? (
          <span className="assessment-reaction-text">TAP NOW!</span>
        ) : state === REACTION_STATES.tooSoon ? (
          <span className="assessment-reaction-text">Too soon — try again</span>
        ) : (
          <span className="assessment-reaction-text">Nicely done!</span>
        )}
      </button>

      <div
        className="assessment-reaction-meta"
        style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--ink-faded, #888)', fontSize: '0.95rem' }}
      >
        <span>Trial {Math.min(trialsDone + (state === REACTION_STATES.go || state === REACTION_STATES.waiting ? 1 : 0), totalTrials)} of {totalTrials}</span>
        {lastMs != null ? <span>{lastMs} ms</span> : <span>&nbsp;</span>}
      </div>
    </div>
  );
}

// =================================================================
// Name-the-animal text-entry screen (Stage 2)
// =================================================================
// Shows a line-art animal (chosen once per mount) and a text input.
// Lenient matching via matchesAnswer. Two points, all-or-nothing.

function TextEntryScreen({ screen, onSubmit }) {
  // Pick the animal once for this mount.
  const animal = useMemo(() => pickAnimal(), []);
  const [value, setValue] = useState('');
  const [submitted, setSubmitted] = useState(false);

  function handleSubmit() {
    if (submitted) return;
    setSubmitted(true);
    const correct = matchesAnswer(value, animal.accepted);
    const earned = correct ? screen.points : 0;
    // Small delay so the resident sees the input register before moving on.
    setTimeout(() => onSubmit(earned, { animal: animal.key }), 250);
  }

  return (
    <div className="assessment-textentry">
      <div className="assessment-q-label">
        Question {screen.qNum} · {screen.points} points
      </div>
      <div className="assessment-q-prompt">{screen.prompt}</div>

      <div
        className="assessment-animal-frame"
        aria-hidden="true"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#FFFFFF',
          border: '1px solid var(--lavender, #F9F3FB)',
          borderRadius: '20px',
          padding: '1rem',
          margin: '1.5rem 0',
        }}
      >
        <AnimalArt animalKey={animal.key} />
      </div>

      <input
        type="text"
        className="assessment-text-input"
        value={value}
        placeholder={screen.placeholder || 'Type your answer'}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
        disabled={submitted}
        autoFocus
        autoComplete="off"
        autoCapitalize="none"
        spellCheck="false"
        style={{
          width: '100%',
          padding: '0.9rem 1rem',
          fontSize: '1.2rem',
          borderRadius: '12px',
          border: '2px solid var(--plum, #33143E)',
          fontFamily: 'var(--font-sans, system-ui, sans-serif)',
          boxSizing: 'border-box',
        }}
      />

      <button
        type="button"
        className="btn assessment-text-submit"
        onClick={handleSubmit}
        disabled={submitted || !value.trim()}
        style={{ marginTop: '1.25rem' }}
      >
        {submitted ? 'Saved' : 'Submit'}
      </button>
    </div>
  );
}

// =================================================================
// Animal line-art (Stage 2)
// =================================================================
// Recognizable line drawings served as static assets from the
// brain-health app's public/ folder (public/animals/<key>.png).
// Add a new animal: drop its PNG in public/animals/ and add an entry
// to ANIMALS in lib/assessment.js.

function AnimalArt({ animalKey }) {
  return (
    <img
      src={`/animals/${animalKey}.png`}
      alt=""
      className="assessment-animal-img"
      style={{
        maxWidth: '100%',
        maxHeight: '260px',
        width: 'auto',
        height: 'auto',
        objectFit: 'contain',
        display: 'block',
      }}
    />
  );
}
